import { Checkbox, Form, Select } from 'antd';
import { type SelectValue } from 'antd/lib/select';
import { flatten, intersection, lowerCase, startCase } from 'lodash';
import { type Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Tooltip } from 'components/ui';
import {
  type AtlasGqlGetReportDeliveryModesQuery,
  useGetReportDeliveryModesQuery,
} from 'types/atlas-graphql';
import { storage } from 'utils/storage';

import { FormActions } from './reportForm.style';
import { ReportParamFields } from './ReportParamFields';
import {
  type DeliveryModeArgumentOption,
  type ReportContext,
  type ReportTemplatesForContext,
} from './types';
import { PICTURE_REPORT_TEMPLATE_IDS, TemplateIdMap } from './utils';

export interface SubmitReportTemplateProp {
  id: string;
  variantId?: string;
}

export interface SubmitAdvancedReportProps {
  deliveryModeId: string;
  reportTemplates: Array<SubmitReportTemplateProp>;
  hermesId?: string;
  shareInHorizonOrganizationId?: string;
  dateFilter?: string;
  weatherStandbyRate?: string;
  siteStandbyRate?: string;
  dailyBillableDelayHours?: string;
}

interface Props {
  templates: ReportTemplatesForContext[];
  onSubmit: (props: SubmitAdvancedReportProps) => Promise<void>;
  onCancel: () => void;
  cacheKey?: string | null;
  reportContexts: ReportContext[];
  shareInHorizonOrganizations?: DeliveryModeArgumentOption[];
  // When true, a picture report template is selectable alongside active Navigator
  // filters, enabling the "Include selected pictures only" checkbox.
  hasActiveImageFilters?: boolean;
  // When false, the active filters match no pictures, so the checkbox is disabled.
  hasFilteredImages?: boolean;
  onIncludeSelectedPicturesOnlyChange?: (includeSelectedPicturesOnly: boolean) => void;
}

const { Option } = Select;

function getFormattedName({ context, name }: { context: string; name: string }) {
  return `${startCase(lowerCase(context))}: ${name}`;
}

function isHermesAPIDeliveryModeSelected({
  selectedId,
  options,
}: {
  selectedId: string;
  options?: AtlasGqlGetReportDeliveryModesQuery['reportDeliveryModes'];
}) {
  return options?.find(mode => mode.id === selectedId)?.name === 'Hermes API';
}

function isShareInHorizonDeliveryModeSelected({
  selectedId,
  options,
}: {
  selectedId: string;
  options?: AtlasGqlGetReportDeliveryModesQuery['reportDeliveryModes'];
}) {
  return options?.find(mode => mode.id === selectedId)?.name === 'Share in Horizon';
}

function isShareInHorizonDeliveryMode({
  modeId,
  options,
}: {
  modeId: string;
  options?: AtlasGqlGetReportDeliveryModesQuery['reportDeliveryModes'];
}) {
  return options?.find(mode => mode.id === modeId)?.name === 'Share in Horizon';
}

function isIncludeCostVariantSelected({
  templates,
  templateIds,
  variantIds,
}: {
  templates?: ReportTemplatesForContext[];
  templateIds?: String[];
  variantIds?: String[];
}) {
  if (!templateIds?.includes(TemplateIdMap.PROJECT_WORK_SUMMARY_V2)) {
    return false;
  }

  const workOrderTemplates = templates?.find(({ context }) => context === 'WORK_ORDER')?.templates;
  const projectWorkSummaryV2Variants = workOrderTemplates?.find(
    ({ template }) => template.id === TemplateIdMap.PROJECT_WORK_SUMMARY_V2
  )?.template?.variants;

  return !!projectWorkSummaryV2Variants
    ?.filter(({ id }) => !!variantIds?.includes(id))
    ?.reduce((prev, { configuration }) => {
      const costsEnabled = !!configuration?.find(({ field }) => field === 'costs')?.isIncluded;
      return prev || costsEnabled;
    }, false);
}

function isProjectWorkSummarySelected({ templateIds }: { templateIds?: String[] }) {
  return !!templateIds?.includes(TemplateIdMap.PROJECT_WORK_SUMMARY_V2);
}

interface VariantIdLookup {
  [key: string]: string;
}

export function AdvancedReportForm(props: Props) {
  const {
    templates,
    onSubmit,
    onCancel,
    cacheKey,
    reportContexts,
    shareInHorizonOrganizations = [],
    hasActiveImageFilters = false,
    hasFilteredImages = true,
    onIncludeSelectedPicturesOnlyChange,
  } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { data, loading: deliveryModesLoading } = useGetReportDeliveryModesQuery();
  const customerId = storage.getItem('customerId');
  const cachedReportConfig = cacheKey
    ? storage.getItem(`reports-${customerId}-${cacheKey}`, true)
    : null;

  let cachedTemplateIds = [],
    cachedDeliveryModeId = null,
    cachedVariantIds = {},
    cachedShareInHorizonOrganizationId = null;

  cachedTemplateIds = cachedReportConfig?.reportTemplates?.map(({ id }) => id) || [];
  cachedDeliveryModeId = cachedReportConfig?.deliveryModeId;
  cachedVariantIds =
    cachedReportConfig?.reportTemplates?.reduce((acc, { id, variantId }) => {
      acc[id] = variantId;
      return acc;
    }, {}) || {};
  cachedShareInHorizonOrganizationId = cachedReportConfig?.shareInHorizonOrganizationId;

  const [templateIds, setTemplateIds] = useState<SelectValue[]>(cachedTemplateIds);
  const [variantIdLookup, setVariantIdLookup] = useState<VariantIdLookup>(cachedVariantIds);
  const [showHermesIdInputField, setShowHermesIdInputField] = useState<boolean>(
    isHermesAPIDeliveryModeSelected({
      selectedId: cachedDeliveryModeId,
      options: data?.reportDeliveryModes,
    })
  );
  const [showShareInHorizonOrganizationField, setShowShareInHorizonOrganizationField] =
    useState<boolean>(
      isShareInHorizonDeliveryModeSelected({
        selectedId: cachedDeliveryModeId,
        options: data?.reportDeliveryModes,
      })
    );
  const [showIncludeCostRates, setShowIncludeCostRates] = useState<boolean>(
    isIncludeCostVariantSelected({
      templates,
      templateIds: cachedTemplateIds,
      variantIds: Object.values(cachedVariantIds),
    })
  );
  const [showDateField, setShowDateField] = useState<boolean>(
    isProjectWorkSummarySelected({ templateIds: cachedTemplateIds })
  );
  const [isValidHermesId, setIsValidHermesId] = useState(false);
  const values = form.getFieldsValue(true);

  useEffect(() => {
    const { deliveryModeId } = values;
    if (!deliveryModeId) {
      return;
    }

    const allTemplates = flatten((templates || []).map(({ templates }) => templates)).map(
      ({ template }) => template
    );

    let clearField = false;
    (templateIds || []).forEach(id => {
      const template = allTemplates.find(t => t.id === id);
      if (!template?.deliveryCompatibility?.some(d => d.id === deliveryModeId)) {
        clearField = true;
      }
    });

    if (clearField) {
      form.setFieldsValue({ deliveryModeId: null });
    }
  }, [form, templateIds, templates, values]);

  // Validate delivery mode when other fields are updated
  useEffect(() => {
    const { deliveryModeId, templateIds, ...rest } = values;
    const pwsVariantId = rest?.[TemplateIdMap.PROJECT_WORK_SUMMARY_V2];

    setShowHermesIdInputField(
      isHermesAPIDeliveryModeSelected({
        selectedId: deliveryModeId,
        options: data?.reportDeliveryModes,
      })
    );
    setShowShareInHorizonOrganizationField(
      isShareInHorizonDeliveryModeSelected({
        selectedId: deliveryModeId,
        options: data?.reportDeliveryModes,
      })
    );

    setShowIncludeCostRates(
      isIncludeCostVariantSelected({
        templates,
        templateIds,
        variantIds: [...(!pwsVariantId ? [] : [pwsVariantId])],
      })
    );

    setShowDateField(isProjectWorkSummarySelected({ templateIds }));
  }, [data?.reportDeliveryModes, templates, values]);

  const handleValuesChange = (changedValues: any) => {
    const fieldName = Object.keys(changedValues)[0];

    if (fieldName === 'templateIds') {
      const value = changedValues[fieldName];
      setTemplateIds(value);
      // Clear delivery mode when templates are cleared
      form.setFieldsValue({ deliveryModeId: null });
      if (!(value || []).some((id: string) => PICTURE_REPORT_TEMPLATE_IDS.includes(id))) {
        form.setFieldsValue({ includeSelectedPicturesOnly: false });
        onIncludeSelectedPicturesOnlyChange?.(false);
      }
    } else if (fieldName === 'deliveryModeId') {
      // Validate delivery mode to toggle visibility of Hermes Inspection ID
      setShowHermesIdInputField(
        isHermesAPIDeliveryModeSelected({
          selectedId: changedValues[fieldName],
          options: data?.reportDeliveryModes,
        })
      );
      setShowShareInHorizonOrganizationField(
        isShareInHorizonDeliveryModeSelected({
          selectedId: changedValues[fieldName],
          options: data?.reportDeliveryModes,
        })
      );
    } else if (fieldName === 'hermesId') {
      setIsValidHermesId(Boolean(changedValues[fieldName]));
    }
  };

  useEffect(() => {
    if (
      !showShareInHorizonOrganizationField ||
      !shareInHorizonOrganizations.length ||
      form.getFieldValue('shareInHorizonOrganizationId')
    ) {
      return;
    }

    form.setFieldsValue({
      shareInHorizonOrganizationId: shareInHorizonOrganizations[0].id,
    });
  }, [form, shareInHorizonOrganizations, showShareInHorizonOrganizationField]);

  useEffect(() => {
    if (!hasActiveImageFilters || !hasFilteredImages) {
      form.setFieldsValue({ includeSelectedPicturesOnly: false });
      onIncludeSelectedPicturesOnlyChange?.(false);
    }
  }, [form, hasActiveImageFilters, hasFilteredImages, onIncludeSelectedPicturesOnlyChange]);

  const handleChangeVariant = (templateId: any, variantId: any) => {
    setVariantIdLookup({
      ...variantIdLookup,
      [templateId]: variantId,
    });
  };

  const handleFinish = ({
    templateIds,
    deliveryModeId,
    hermesId,
    shareInHorizonOrganizationId,
    dateFilter,
    weatherStandbyRate,
    siteStandbyRate,
    dailyBillableDelayHours,
  }: {
    templateIds: string[];
    deliveryModeId: string;
    hermesId?: string;
    shareInHorizonOrganizationId?: string;
    dateFilter?: Moment;
    weatherStandbyRate?: number;
    siteStandbyRate?: number;
    dailyBillableDelayHours?: number;
  }) => {
    const reportTemplates = templateIds.map((id: string) => ({
      id,
      variantId: variantIdLookup[id],
    }));
    if (cacheKey) {
      storage.setItem(
        `reports-${customerId}-${cacheKey}`,
        { reportTemplates, deliveryModeId, shareInHorizonOrganizationId },
        true
      );
    }

    return onSubmit({
      deliveryModeId,
      reportTemplates,
      hermesId,
      shareInHorizonOrganizationId,
      dateFilter: dateFilter?.format('YYYY-MM-DD'),
      weatherStandbyRate: weatherStandbyRate?.toString(),
      siteStandbyRate: siteStandbyRate?.toString(),
      dailyBillableDelayHours: dailyBillableDelayHours?.toString(),
    });
  };

  const isPictureReportSelected = (templateIds || []).some(id =>
    PICTURE_REPORT_TEMPLATE_IDS.includes(id as string)
  );

  const reportDeliveryModes = data?.reportDeliveryModes;

  const variantDropdowns = templateIds
    ?.map(tId => {
      const allTemplates = flatten((templates || []).map(({ templates }) => templates));
      const thisTemplate = allTemplates.find(t => t.template.id === tId);

      const template = thisTemplate?.template;
      const variants = template?.variants;

      if (!template || !variants || !variants.length) {
        return null;
      }

      return (
        <Form.Item
          label={t('reports.variantLabel', '{{name}} variant', {
            name: getFormattedName({ context: template.context, name: template.name }),
          })}
          name={template.id}
          key={template.id}
        >
          <Select
            allowClear
            placeholder={t('reports.variantPlaceholder', 'Leave blank for default')}
            onChange={variantId => handleChangeVariant(tId, variantId)}
          >
            {variants.map(({ id, name }: { id: string; name: string }) => {
              return (
                <Option key={id} value={id}>
                  {name}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
      );
    })
    .filter(Boolean);

  const deliveryModeOptions = useMemo(() => {
    const allTemplates = flatten((templates || []).map(({ templates }) => templates));
    const activeTemplates = allTemplates.filter(t => (templateIds || []).includes(t.template.id));

    // Only allow use of delivery modes that are compatible with all the selected
    // report templates. (ie. the intersection)
    const allowedDeliveryModes = intersection(
      ...activeTemplates.map(t => (t.template.deliveryCompatibility || []).map(({ id }) => id))
    );

    return (reportDeliveryModes || []).map(({ id, name }) => {
      const disabled =
        !allowedDeliveryModes.includes(id) ||
        (isShareInHorizonDeliveryMode({
          modeId: id,
          options: reportDeliveryModes,
        }) &&
          !shareInHorizonOrganizations.length);
      return (
        <Option key={id} value={id} disabled={disabled}>
          <Tooltip
            title={
              disabled
                ? isShareInHorizonDeliveryMode({
                    modeId: id,
                    options: reportDeliveryModes,
                  }) && !shareInHorizonOrganizations.length
                  ? t(
                      'reports.noOrgsForShareInHorizon',
                      'No task Organizations are available for Share in Horizon delivery.'
                    )
                  : t(
                      'reports.templatesIncompatibleWithDelivery',
                      'Some templates are not compatible with delivery by {{name}}',
                      { name }
                    )
                : null
            }
          >
            {name}
          </Tooltip>
        </Option>
      );
    });
  }, [reportDeliveryModes, shareInHorizonOrganizations.length, templateIds, templates, t]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onValuesChange={handleValuesChange}
      initialValues={{
        templateIds: cachedTemplateIds,
        deliveryModeId: cachedDeliveryModeId,
        shareInHorizonOrganizationId: cachedShareInHorizonOrganizationId,
        dailyBillableDelayHours: 8,
        ...cachedVariantIds,
      }}
    >
      <Form.Item
        key="templateIds"
        label={t('settings.templates', 'Templates')}
        name="templateIds"
        rules={[
          {
            required: true,
            message: t('reports.selectTemplatesRequired', 'Please select one or more templates.'),
          },
        ]}
      >
        <Select
          allowClear={true}
          mode="multiple"
          placeholder={t('reports.selectReportTemplates', 'Select report templates')}
        >
          {flatten((templates || []).map(({ templates }) => templates)).map(
            ({ template, disabled }) => {
              const { id, name, itemLimit, context } = template;
              const matchingContext = reportContexts.find(
                reportContext => reportContext?.context === context
              );
              return (
                <Option key={id} value={id} disabled={disabled}>
                  <Tooltip
                    title={
                      disabled
                        ? matchingContext?.toolTip
                          ? matchingContext?.toolTip
                          : t(
                              'reports.reportsLimitedToItems',
                              '{{name}} reports are limited to {{itemLimit}} items',
                              {
                                name: getFormattedName({ context, name }),
                                itemLimit,
                              }
                            )
                        : null
                    }
                  >
                    {getFormattedName({ context, name })}
                  </Tooltip>
                </Option>
              );
            }
          )}
        </Select>
      </Form.Item>
      {variantDropdowns}
      {hasActiveImageFilters && isPictureReportSelected ? (
        <Form.Item
          key="includeSelectedPicturesOnly"
          name="includeSelectedPicturesOnly"
          valuePropName="checked"
        >
          <Checkbox
            disabled={!hasFilteredImages}
            onChange={({ target: { checked } }) => onIncludeSelectedPicturesOnlyChange?.(checked)}
          >
            <Tooltip
              title={
                hasFilteredImages
                  ? undefined
                  : t('reports.noFilteredImages', 'No pictures match the current Navigator filters')
              }
            >
              <span>
                {t('reports.includeSelectedPicturesOnly', 'Include selected pictures only')}
              </span>
            </Tooltip>
          </Checkbox>
        </Form.Item>
      ) : undefined}
      {templateIds?.length ? (
        <Form.Item
          key="deliveryModeId"
          label={t('reports.deliveryMode', 'Delivery Mode')}
          name="deliveryModeId"
        >
          <Select
            allowClear
            placeholder={t('reports.selectDeliveryMode', 'Select delivery mode')}
            loading={deliveryModesLoading}
          >
            {deliveryModeOptions}
          </Select>
        </Form.Item>
      ) : undefined}
      <ReportParamFields
        showHermesId={showHermesIdInputField}
        showShareInHorizonOrganization={showShareInHorizonOrganizationField}
        showCostRates={showIncludeCostRates}
        showDateFilter={showDateField}
        shareInHorizonOrganizations={shareInHorizonOrganizations}
      />
      <FormActions>
        <Button
          type="primary"
          htmlType="submit"
          disabled={
            showHermesIdInputField ? !templateIds?.length || !isValidHermesId : !templateIds?.length
          }
        >
          {t('common.create', 'Create')}
        </Button>
        <Button onClick={onCancel}>{t('common.cancel', 'Cancel')}</Button>
      </FormActions>
    </Form>
  );
}
